import { userProfileValues } from "@components/Forms/UserProfileForm";
import { createApi } from "@reduxjs/toolkit/query/react";
import {
  DefaultMessageResponse,
  DefaultResponse,
  QueryParams,
  UserGlobalTicketResponse,
  UserProfileResponse,
  UserPurchaseHistoryResponse,
  UserReservationResponse,
} from "types/index";
import { LoyaltyEarningStatusResponse } from "types/loyalty";
import { axiosBaseQuery, axiosService } from "@picovico/helpers";
import { getLocalStorageData } from "@picovico/utils";
import type { WhatsAppOtpRequestBody, WhatsAppOtpSent } from "custom-types";
import { parseRetryAfter, retryAfterSeconds } from "utils/whatsapp";

/**
 * What the OTP request rejects with: the response body (as every other
 * request here), plus the HTTP status as `http_status` (a name the shared
 * error parsers ignore, so other refusals read as before) and `retry_after`
 * in seconds, from the body or else the `Retry-After` header (D18 sends both).
 */
function otpRequestError(failure: unknown): Record<string, unknown> {
  const response = failure as {
    data?: unknown;
    status?: unknown;
    headers?: Record<string, unknown>;
  } | null;
  const body =
    response?.data && typeof response.data === "object"
      ? { ...(response.data as Record<string, unknown>) }
      : {};
  const retryAfter =
    retryAfterSeconds(body.retry_after) ??
    parseRetryAfter(response?.headers?.["retry-after"]);
  return {
    ...body,
    ...(typeof response?.status === "number"
      ? { http_status: response.status }
      : {}),
    ...(retryAfter ? { retry_after: retryAfter } : {}),
  };
}

export const userAPI = createApi({
  baseQuery: axiosBaseQuery(),
  tagTypes: ["user-detail"],
  reducerPath: "userAPI",
  endpoints: (builder) => ({
    updateUserProfile: builder.mutation<
      DefaultMessageResponse,
      Partial<userProfileValues> | FormData
    >({
      query: (data) => ({
        name: "user_profile_update",
        method: "PATCH",
        data,
      }),
      invalidatesTags: ["user-detail"],
    }),
    getUserProfile: builder.query<UserProfileResponse, QueryParams>({
      query: (queryParams) => ({
        name: "user_profile",
        method: "GET",
        ...queryParams,
      }),
      providesTags: ["user-detail"],
    }),
    getUserPurchaseHistory: builder.query<
      UserPurchaseHistoryResponse,
      QueryParams
    >({
      query: (queryParams) => ({
        name: "user-history",
        method: "GET",
        params: {
          ...queryParams,
        },
      }),
    }),
    getUserReservations: builder.query<UserReservationResponse, QueryParams>({
      query: (queryParams) => ({
        name: "user-reservations",
        method: "GET",
        ...queryParams,
      }),
    }),
    /**
     * Sends a code to the profile mobile: by SMS, or on WhatsApp when the
     * company has `whatsapp.otp` (claude/design/whatsapp-messaging/02 §10).
     * Without a `channel` the body is empty, as before WhatsApp, and the
     * server picks; `{channel: "sms"}` is "Send by SMS instead". The answer
     * may add `channel` and `fallback_channels` (`readOtpSent`).
     *
     * A query function over the shared axios instance rather than the base
     * query, which keeps only the body: a 429 `OTP_THROTTLED` must keep its
     * `Retry-After` header. It rejects with the body plus `http_status` and
     * `retry_after` (the body's, else the header's), read by
     * `otpThrottleMessage`.
     */
    getMobileVerificationOtp: builder.query<
      DefaultResponse<WhatsAppOtpSent>,
      WhatsAppOtpRequestBody | void
    >({
      queryFn: async (body) => {
        try {
          const response = await axiosService({
            name: "get-mobile-verification-otp",
            method: "POST",
            ...(body?.channel ? { data: { channel: body.channel } } : {}),
          });
          return { data: response.data };
        } catch (failure) {
          return { error: otpRequestError(failure) };
        }
      },
    }),
    verifyMobileVerifiCationOtp: builder.mutation<
      DefaultMessageResponse,
      { verification_id: string | null; code: string }
    >({
      query: (data) => ({
        name: "verify-mobile-verification-otp",
        method: "POST",
        data,
      }),
      invalidatesTags: ["user-detail"],
    }),
    getUserLoyaltyPointsList: builder.query({
      query: (queryParams) => ({
        name: "user-loyalty-points",
        method: "GET",
        ...queryParams,
      }),
    }),
    // Live standing against the earning caps, sourced from dolby so the checkout's projection
    // matches what will actually be awarded. Re-read per checkout — the windows move.
    getLoyaltyEarningStatus: builder.query<LoyaltyEarningStatusResponse, QueryParams>({
      query: (queryParams) => ({
        name: "loyalty-earning-status",
        method: "GET",
        params: { ...queryParams },
      }),
      providesTags: ["user-detail"],
    }),
    getUserGlobalTicketList: builder.query<UserGlobalTicketResponse, QueryParams>({
      query: (queryParams) => ({
        name: "get_global_ticket",
        method: "GET",
        headers: {
          cinemaHall: getLocalStorageData("cinema_hall") || "",
        },
        params: { ...queryParams },
      }),
    }),
  }),
});

export const {
  useUpdateUserProfileMutation,
  useGetUserProfileQuery,
  useLazyGetUserProfileQuery,
  useGetUserPurchaseHistoryQuery,
  useLazyGetUserPurchaseHistoryQuery,
  useGetUserReservationsQuery,
  useGetMobileVerificationOtpQuery,
  useLazyGetMobileVerificationOtpQuery,
  useVerifyMobileVerifiCationOtpMutation,
  useGetUserLoyaltyPointsListQuery,
  useGetLoyaltyEarningStatusQuery,
  useLazyGetUserGlobalTicketListQuery,
} = userAPI;
